import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  GoogleAuthProvider,
  User,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { FirebaseError } from 'firebase/app';
import { auth, db } from '../firebase';
import { AppView, WhitelistStatus } from '../types/app';

function getAuthMessage(error: unknown): string {
  if (error instanceof FirebaseError) {
    switch (error.code) {
      case 'auth/invalid-email':
        return 'Ugyldigt e-mailformat.';
      case 'auth/invalid-credential':
        return 'Forkert e-mail eller adgangskode.';
      case 'auth/user-not-found':
        return 'Ingen konto fundet med den e-mail.';
      case 'auth/wrong-password':
        return 'Forkert e-mail eller adgangskode.';
      case 'auth/email-already-in-use':
        return 'Denne e-mail er allerede i brug.';
      case 'auth/weak-password':
        return 'Adgangskoden skal være mindst 6 tegn.';
      case 'auth/popup-closed-by-user':
        return 'Login-popup blev lukket før den blev fuldført.';
      case 'auth/configuration-not-found':
        return 'Loginmetoden er ikke konfigureret i Firebase Console for dette projekt.';
      case 'auth/operation-not-allowed':
        return 'Denne loginmetode er deaktiveret i Firebase Authentication.';
      default:
        return error.message;
    }
  }

  return 'Login fejlede. Prøv igen.';
}

function getAdminEmails(): string[] {
  const configuredEmails = process.env.REACT_APP_ADMIN_EMAILS || '';
  return configuredEmails
    .split(',')
    .map((emailValue) => emailValue.trim().toLowerCase())
    .filter(Boolean);
}

export function useAuth() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authError, setAuthError] = useState('');
  const [whitelistStatus, setWhitelistStatus] = useState<WhitelistStatus>('unknown');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(false);
  const [view, setView] = useState<AppView>('home');

  const googleProvider = useMemo(() => new GoogleAuthProvider(), []);
  const hasAppAccess = whitelistStatus === 'allowed' || isAdmin;

  const checkAdminAccess = useCallback(async (user: User) => {
    setIsCheckingAdmin(true);
    try {
      const token = await user.getIdTokenResult(true);
      const userEmail = (user.email || '').toLowerCase();
      const isClaimAdmin = token.claims.admin === true;
      const isEmailAdmin = getAdminEmails().includes(userEmail);
      setIsAdmin(isClaimAdmin || isEmailAdmin);
    } catch (error: unknown) {
      setIsAdmin(false);
    } finally {
      setIsCheckingAdmin(false);
    }
  }, []);

  const loadWhitelistStatus = useCallback(async (user: User) => {
    setWhitelistStatus('loading');

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));

      if (!userDoc.exists()) {
        setWhitelistStatus('pending');
        return;
      }

      const data = userDoc.data() as { whitelisted?: boolean };
      setWhitelistStatus(data.whitelisted ? 'allowed' : 'pending');
    } catch (error: unknown) {
      setAuthError('Du er logget ind, men whitelist-status kunne ikke hentes. Prøv igen.');
      setWhitelistStatus('pending');
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (!user) {
        setWhitelistStatus('unknown');
        setIsAdmin(false);
        setView('home');
      }
      setIsLoadingUser(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    setAuthError('');
    void loadWhitelistStatus(currentUser);
    void checkAdminAccess(currentUser);
  }, [currentUser, loadWhitelistStatus, checkAdminAccess]);

  useEffect(() => {
    if (!isAdmin && view === 'admin') {
      setView('home');
    }
  }, [isAdmin, view]);

  const handleEmailAuth = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAuthError('');
    setIsSubmitting(true);

    try {
      if (isRegisterMode) {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
      setPassword('');
    } catch (error: unknown) {
      setAuthError(getAuthMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setAuthError('');
    setIsSubmitting(true);

    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error: unknown) {
      setAuthError(getAuthMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    setAuthError('');
    setIsSubmitting(true);

    try {
      await signOut(auth);
    } catch (error: unknown) {
      setAuthError(getAuthMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefreshAccess = async () => {
    if (!currentUser) {
      return;
    }

    setAuthError('');
    await loadWhitelistStatus(currentUser);
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    isRegisterMode,
    setIsRegisterMode,
    currentUser,
    isLoadingUser,
    isSubmitting,
    authError,
    setAuthError,
    whitelistStatus,
    isAdmin,
    isCheckingAdmin,
    view,
    setView,
    hasAppAccess,
    handleEmailAuth,
    handleGoogleSignIn,
    handleSignOut,
    handleRefreshAccess,
  };
}
